(function cartDrawerController(root) {
  'use strict';

  function cartApiPath(path) {
    const routeRoot = root.Shopify?.routes?.root || '/';
    const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
    const normalizedPath = String(path || '').replace(/^\//, '');

    return `${normalizedRoot}${normalizedPath}`;
  }

  function parseCartError(payload, fallback) {
    if (typeof payload === 'string') {
      return payload;
    }

    return payload?.description || payload?.message || fallback;
  }

  function normalizeCartQuantity(value) {
    const quantity = Number(value);

    if (!Number.isFinite(quantity) || quantity <= 0) {
      return 0;
    }

    return Math.floor(quantity);
  }

  function formatPaddedQuantity(value) {
    return String(normalizeCartQuantity(value)).padStart(2, '0');
  }

  root.cartDrawer = function cartDrawer(config = {}) {
    return {
      open: false,
      loading: false,
      updatingLine: null,
      removingLine: null,
      editingLine: null,
      errorMessage: '',
      sectionId: config.sectionId || 'cart-drawer',
      updateError: config.updateError || '',
      previouslyFocused: null,
      dragging: false,
      swipeClosing: false,
      dragPointerId: null,
      dragStartY: 0,
      dragOffsetY: 0,
      dragStartedAt: 0,
      scrollOverflowing: false,
      scrollAtEnd: true,
      scrollThumbHeight: 0,
      scrollThumbOffset: 0,
      scrollResizeObserver: null,
      refreshController: null,
      refreshSequence: 0,

      init() {
        this.syncCartCount({ item_count: Number(root.document.querySelector('.site-header__cart-count')?.textContent) || 0 });
        this.$nextTick(() => {
          root.cartInventory?.prioritizeCartLineItems(this.$root);
          this.observeScrollArea();
          this.updateScrollState();
        });
      },

      destroy() {
        this.scrollResizeObserver?.disconnect();
        this.refreshController?.abort();
        root.themeModalAccessibility?.deactivate(this.$root);
      },

      async openDrawer() {
        const wasOpen = this.open;
        if (!wasOpen) {
          this.previouslyFocused = root.document.activeElement;
          this.open = true;
          root.themeModalAccessibility?.activate(this.$root);
          root.dispatchEvent(new CustomEvent('cart-drawer:opened'));
        }

        await this.refresh();
        if (!wasOpen) {
          this.$nextTick(() => this.$refs.closeButton?.focus());
        }
      },

      closeDrawer(restoreFocus = true) {
        if (!this.open) {
          return;
        }

        this.cancelDrag();
        this.editingLine = null;
        this.open = false;
        root.themeModalAccessibility?.deactivate(this.$root);
        this.errorMessage = '';
        root.dispatchEvent(new CustomEvent('cart-drawer:closed'));
        this.$nextTick(() => {
          if (restoreFocus && this.previouslyFocused instanceof HTMLElement) {
            this.previouslyFocused.focus();
          }
        });
      },

      async handleCartUpdated(event) {
        const cart = event?.detail?.cart;
        const message = event?.detail?.message || '';
        if (cart) {
          this.syncCartCount(cart);
        }

        if (event?.detail?.source === 'cart-drawer') {
          this.errorMessage = message;
          return;
        }

        if (event?.detail?.source === 'cart-page' || event?.detail?.openDrawer === false) {
          if (this.open) {
            await this.refresh();
            this.errorMessage = message;
          }
          return;
        }

        await this.openDrawer();
        this.errorMessage = message;
      },

      async handleCartError(event) {
        const cart = event?.detail?.cart;
        const message = event?.detail?.message || this.updateError;

        if (cart) {
          this.syncCartCount(cart);
        }

        await this.openDrawer();
        this.errorMessage = message;
      },

      async refresh() {
        const requestSequence = this.refreshSequence + 1;
        this.refreshSequence = requestSequence;
        this.refreshController?.abort();
        const requestController = typeof root.AbortController === 'function'
          ? new root.AbortController()
          : null;
        this.refreshController = requestController;
        this.loading = true;
        this.errorMessage = '';

        try {
          const url = new URL(cartApiPath('/'), root.location.origin);
          url.searchParams.set('section_id', this.sectionId);
          const requestOptions = {
            headers: { Accept: 'text/html' },
          };
          if (requestController) {
            requestOptions.signal = requestController.signal;
          }
          const response = await root.fetch(url.toString(), requestOptions);

          if (!response.ok) {
            throw new Error(this.updateError);
          }

          const html = await response.text();
          const nextDocument = new DOMParser().parseFromString(html, 'text/html');
          const nextContent = nextDocument.querySelector('[data-cart-drawer-content]');
          const currentContent = this.$root.querySelector('[data-cart-drawer-content]');

          if (!nextContent || !currentContent) {
            throw new Error(this.updateError);
          }

          if (requestSequence !== this.refreshSequence || requestController?.signal.aborted) {
            return;
          }

          currentContent.innerHTML = nextContent.innerHTML;
          root.cartInventory?.prioritizeCartLineItems(currentContent);
          this.editingLine = null;
          this.$nextTick(() => {
            if (typeof root.configureShippingProtection === 'function') {
              root.configureShippingProtection();
            }
            this.observeScrollArea();
            this.updateScrollState();
          });
        } catch (error) {
          if (error?.name !== 'AbortError' && requestSequence === this.refreshSequence) {
            this.errorMessage = parseCartError(error, this.updateError);
          }
        } finally {
          if (requestSequence === this.refreshSequence) {
            this.loading = false;
            this.refreshController = null;
          }
        }
      },

      async updateLine(line, quantity, lineKey = '') {
        if (this.updatingLine !== null || this.removingLine !== null) {
          return;
        }

        this.updatingLine = line;
        this.errorMessage = '';

        try {
          if (!root.cartInventory) {
            throw new Error(this.updateError);
          }

          const result = await root.cartInventory.changeLine(lineKey || line, quantity);
          const cart = result.cart;
          this.syncCartCount(cart);
          await this.refresh();
          this.errorMessage = result.message || '';
          root.document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart, source: 'cart-drawer' },
          }));
        } catch (error) {
          const errorMessage = parseCartError(error, this.updateError);
          if (error?.cart) {
            this.syncCartCount(error.cart);
            root.document.dispatchEvent(new CustomEvent('cart:updated', {
              detail: { cart: error.cart, source: 'cart-drawer' },
            }));
          }
          await this.refresh();
          this.errorMessage = errorMessage;
        } finally {
          this.updatingLine = null;
        }
      },

      async commitQuantity(event, line, currentQuantity, maximumQuantity = null, lineKey = '') {
        const input = event?.currentTarget;
        const previousQuantity = Math.max(normalizeCartQuantity(currentQuantity), 1);
        const requestedQuantity = normalizeCartQuantity(input?.value);
        const nextQuantity = root.cartInventory
          ? root.cartInventory.clampLineQuantity(requestedQuantity, maximumQuantity, previousQuantity)
          : requestedQuantity || previousQuantity;

        if (input) {
          input.value = formatPaddedQuantity(nextQuantity);
        }

        if (nextQuantity === previousQuantity) {
          return;
        }

        await this.updateLine(line, nextQuantity, lineKey);
      },

      async removeLine(line, lineKey = '') {
        if (this.updatingLine !== null || this.removingLine !== null) {
          return;
        }

        this.removingLine = line;
        const reducedMotion = root.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reducedMotion) {
          await new Promise((resolve) => root.setTimeout(resolve, 180));
        }

        this.removingLine = null;
        await this.updateLine(line, 0, lineKey);
      },

      isMobileSwipe() {
        return root.matchMedia('(max-width: 749px)').matches;
      },

      startDrag(event) {
        const dragTarget = event.target.closest('.cart-drawer__header, .cart-drawer__handle');
        const interactiveTarget = event.target.closest('button, a, input, select, textarea');
        if (!this.isMobileSwipe() || !dragTarget || interactiveTarget || this.swipeClosing) {
          return;
        }

        this.dragging = true;
        this.dragPointerId = event.pointerId;
        this.dragStartY = event.clientY;
        this.dragStartedAt = Date.now();
        this.dragOffsetY = 0;
        this.$refs.panel?.setPointerCapture?.(event.pointerId);
      },

      moveDrag(event) {
        if (!this.dragging || event.pointerId !== this.dragPointerId) {
          return;
        }

        this.dragOffsetY = Math.max(0, event.clientY - this.dragStartY);
        if (this.dragOffsetY > 0) {
          event.preventDefault();
        }
      },

      async endDrag(event) {
        if (!this.dragging || event.pointerId !== this.dragPointerId) {
          return;
        }

        const elapsed = Math.max(Date.now() - this.dragStartedAt, 1);
        const velocity = this.dragOffsetY / elapsed;
        const panelHeight = this.$refs.panel?.offsetHeight || 0;
        const closeDistance = Math.min(120, panelHeight * 0.18);
        const shouldClose = this.dragOffsetY >= closeDistance || (this.dragOffsetY >= 24 && velocity >= 0.55);

        this.releaseDragPointer();
        this.dragging = false;

        if (!shouldClose) {
          this.dragOffsetY = 0;
          return;
        }

        this.swipeClosing = true;
        this.dragOffsetY = panelHeight + 8;
        const reducedMotion = root.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reducedMotion) {
          await new Promise((resolve) => root.setTimeout(resolve, 280));
        }

        if (!this.swipeClosing || !this.open) {
          return;
        }

        this.open = false;
        root.themeModalAccessibility?.deactivate(this.$root);
        this.errorMessage = '';
        root.dispatchEvent(new CustomEvent('cart-drawer:closed'));
        this.$nextTick(() => {
          if (this.previouslyFocused instanceof HTMLElement) {
            this.previouslyFocused.focus();
          }
        });
        root.setTimeout(() => {
          this.swipeClosing = false;
          this.dragOffsetY = 0;
        }, reducedMotion ? 0 : 220);
      },

      releaseDragPointer() {
        if (this.dragPointerId === null) {
          return;
        }

        try {
          this.$refs.panel?.releasePointerCapture?.(this.dragPointerId);
        } catch (_error) {
          // The pointer may already have been released by the browser.
        }
        this.dragPointerId = null;
      },

      cancelDrag() {
        this.releaseDragPointer();
        this.dragging = false;
        this.swipeClosing = false;
        this.dragOffsetY = 0;
      },

      panelDragStyle() {
        if (!this.dragging && !this.swipeClosing && this.dragOffsetY === 0) {
          return '';
        }

        return `transform: translateY(${this.dragOffsetY}px)`;
      },

      observeScrollArea() {
        this.scrollResizeObserver?.disconnect();
        this.scrollResizeObserver = null;

        if (!this.$refs.scrollArea || typeof root.ResizeObserver !== 'function') {
          return;
        }

        this.scrollResizeObserver = new root.ResizeObserver(() => this.updateScrollState());
        this.scrollResizeObserver.observe(this.$refs.scrollArea);
        Array.from(this.$refs.scrollArea.children).forEach((child) => {
          this.scrollResizeObserver.observe(child);
        });
      },

      updateScrollState() {
        const scrollArea = this.$refs.scrollArea;
        if (!scrollArea) {
          this.scrollOverflowing = false;
          this.scrollAtEnd = true;
          this.scrollThumbHeight = 0;
          this.scrollThumbOffset = 0;
          return;
        }

        this.scrollOverflowing = scrollArea.scrollHeight > scrollArea.clientHeight + 1;
        this.scrollAtEnd = !this.scrollOverflowing
          || scrollArea.scrollTop + scrollArea.clientHeight >= scrollArea.scrollHeight - 2;

        if (!this.scrollOverflowing) {
          this.scrollThumbHeight = scrollArea.clientHeight;
          this.scrollThumbOffset = 0;
          return;
        }

        const scrollRange = scrollArea.scrollHeight - scrollArea.clientHeight;
        this.scrollThumbHeight = Math.max(
          48,
          Math.round((scrollArea.clientHeight / scrollArea.scrollHeight) * scrollArea.clientHeight),
        );
        this.scrollThumbOffset = Math.round(
          (scrollArea.scrollTop / scrollRange) * (scrollArea.clientHeight - this.scrollThumbHeight),
        );
      },

      handleScroll() {
        this.updateScrollState();
      },

      syncCartCount(cart) {
        if (!cart || typeof cart.item_count === 'undefined') {
          return;
        }

        const count = String(cart.item_count);
        const template = root.theme?.cartCountTemplate || '';
        const label = template.replace('__count__', count);

        root.document.querySelectorAll('.site-header__cart-count').forEach((badge) => {
          badge.textContent = count;
          if (label) {
            badge.setAttribute('aria-label', label);
          }
        });
      },

      trapFocus(event) {
        const focusable = Array.from(this.$refs.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        )).filter((element) => element.offsetParent !== null);

        if (focusable.length === 0) {
          event.preventDefault();
          return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && root.document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && root.document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },
    };
  };
}(window));
